<div class="flex flex-col gap-4 w-full">
  <!-- Campaign Health card -->
  <div class="rounded-2xl bg-shikenso-gray-base-24 px-6 py-3 flex flex-col gap-4">
    <span class="font-bold text-base" data-testid="campaign-details-overview-health-title">{{
      'pages.campaignDetails.overview.campaignHealth' | translate
    }}</span>

    <div class="flex items-start gap-6">
      <!-- Content Status -->
      <div class="flex flex-col gap-1 w-[248px] min-w-[248px]">
        <span class="text-sm">
          {{ 'pages.campaignDetails.overview.contentStatus' | translate }}
        </span>

        @if (loading()) {
          <ngx-skeleton-loader
            class="flex h-6 mb-1"
            [theme]="{ height: '24px', width: '67px', 'border-radius': '100vw' }"
          />
        } @else {
          <app-status-badge class="mb-1" colorScheme="campaignHealth" [status]="contentHealthStatus()" />
        }

        @if (missingContentsCount() > 0) {
          <button
            type="button"
            class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
            (click)="onMissingContentsClicked()"
          >
            <span class="w-2.5 h-2.5 rounded-full bg-shikenso-error-bright shrink-0"></span>
            <span class="text-xs">
              {{ 'pages.campaignDetails.overview.missingContents' | translate: { count: missingContentsCount() } }}
            </span>
          </button>
        }

        @if (failedContentsCount() > 0) {
          <button
            type="button"
            class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
            (click)="onFailedContentsClicked()"
          >
            <span class="w-2.5 h-2.5 rounded-full bg-shikenso-error-bright shrink-0"></span>
            <span class="text-xs">
              {{ 'pages.campaignDetails.overview.failedContents' | translate: { count: failedContentsCount() } }}
            </span>
          </button>
        }

        @if (problemContentsCount() > 0) {
          <button
            type="button"
            class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
            (click)="onProblemContentsClicked()"
          >
            <span class="w-2.5 h-2.5 rounded-full bg-shikenso-warning-base shrink-0"></span>
            <span class="text-xs">
              {{ 'pages.campaignDetails.overview.problemContents' | translate: { count: problemContentsCount() } }}
            </span>
          </button>
        }
      </div>

      <!-- KPI Goal Status -->
      <div class="flex flex-col gap-1">
        <span class="text-sm">
          {{ 'pages.campaignDetails.overview.kpiGoalStatus' | translate }}
        </span>

        @if (loading()) {
          <ngx-skeleton-loader
            class="flex h-6 mb-1"
            [theme]="{ height: '24px', width: '74px', 'border-radius': '100vw' }"
          />
        } @else {
          <app-status-badge class="mb-1" colorScheme="kpiGoals" [status]="kpiGoalHealthStatus()" />
        }

        <div class="flex flex-col gap-1">
          @if (goalsBelowTargetCount() > 0) {
            <button
              type="button"
              class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
              (click)="onGoalsBelowTargetClicked()"
            >
              <span class="w-2.5 h-2.5 rounded-full bg-shikenso-warning-base shrink-0"></span>
              <span class="text-xs">
                {{ 'pages.campaignDetails.overview.goalsBelowTarget' | translate: { count: goalsBelowTargetCount() } }}
              </span>
            </button>
          }

          @if (goalsUnavailableCount() > 0) {
            <button
              type="button"
              class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
              (click)="onGoalsUnavailableClicked()"
            >
              <span class="w-2.5 h-2.5 rounded-full bg-shikenso-error-bright shrink-0"></span>
              <span class="text-xs">
                {{ 'pages.campaignDetails.overview.goalsUnavailable' | translate: { count: goalsUnavailableCount() } }}
              </span>
            </button>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Content Execution card (Content Types + Matched Content Status) -->
  @if (campaignExportConfig().showOverviewContentExecution) {
    <div class="rounded-2xl bg-shikenso-gray-base-24 p-6 flex flex-col gap-3 mt-4">
      <div class="flex items-center gap-2">
        <span class="font-bold text-xl">{{ 'pages.campaignDetails.overview.contentExecution' | translate }}</span>
        <app-icon
          src="/icons/info-circle.svg"
          alt="info-circle"
          matTooltipPosition="above"
          appTrackInfoHover="content_execution"
          [size]="16"
          [matTooltip]="'pages.campaignDetails.overview.contentExecutionTooltip' | translate"
          [currentTab]="currentTab"
        />
      </div>

      <div class="flex items-stretch gap-8">
        <!-- Content Types graph -->
        <div class="flex flex-col flex-1">
          <span class="font-bold">{{ 'pages.campaignDetails.overview.contentTypes' | translate }}</span>
          <div class="flex items-center gap-10">
            <!-- graph + progress label stacked -->
            <div class="flex flex-col items-center gap-2">
              <app-circular-graph
                totalCountFontClass="font-bold text-[36px] leading-none"
                totalLabelFontClass="text-sm"
                statusType="content"
                [data]="contentTypesData()"
                [loading]="loading()"
                [includeBonusInMainSeries]="true"
                [showTotal]="true"
                [chartSize]="248"
                [showPercentage]="false"
                [totalCountCentered]="true"
                (segmentClicked)="onContentTypeSegmentClicked($event)"
              />

              <div
                class="text-xs text-shikenso-purple-base-40 flex items-center gap-2 min-h-5 cursor-pointer rounded px-1 hover:bg-shikenso-pink-base-12"
                (click)="onContentTypesProgressClicked()"
              >
                {{ 'pages.campaignDetails.overview.progress' | translate }}

                @if (loading()) {
                  <ngx-skeleton-loader
                    class="flex h-4"
                    [theme]="{ height: '16px', width: '28px', 'border-radius': '4px' }"
                  />
                } @else {
                  <span class="text-sm text-shikenso-purple-base">
                    {{ contentTypesMatchedCount() }} / {{ contentTypesProgressTotal() }}
                  </span>
                }
              </div>
            </div>
            <!-- legend centered relative to the graph -->
            <div class="flex flex-col gap-2 self-stretch w-full justify-center">
              @if (isMissingContentTypeVisible()) {
                <ng-container
                  *ngTemplateOutlet="
                    legendItem;
                    context: {
                      colorClass: 'bg-shikenso-error-bright',
                      labelKey: 'shared.statuses.missing',
                      count: contentTypesData().failed,
                      minWidth: 140,
                      maxWidth: 152,
                      fontClass: 'text-base',
                      dotSizeClass: 'w-3 h-3',
                      clickFn: onContentTypeLegendClicked.bind(this, 'failed'),
                    }
                  "
                />
              }
              @if (isMatchedContentTypeVisible()) {
                <ng-container
                  *ngTemplateOutlet="
                    legendItem;
                    context: {
                      colorClass: 'bg-shikenso-success-bright',
                      labelKey: 'shared.statuses.matched',
                      count: contentTypesData().success,
                      minWidth: 140,
                      maxWidth: 152,
                      fontClass: 'text-base',
                      dotSizeClass: 'w-3 h-3',
                      clickFn: onContentTypeLegendClicked.bind(this, 'success'),
                    }
                  "
                />
              }
              @if (isBonusContentTypeVisible()) {
                <ng-container
                  *ngTemplateOutlet="
                    legendItem;
                    context: {
                      colorClass: 'bg-shikenso-pink-base',
                      labelKey: 'shared.statuses.bonus',
                      count: contentTypesData().bonus,
                      minWidth: 140,
                      maxWidth: 152,
                      fontClass: 'text-base',
                      dotSizeClass: 'w-3 h-3',
                      clickFn: onContentTypeLegendClicked.bind(this, 'bonus'),
                    }
                  "
                />
              }
              @if (isPlannedContentTypeVisible()) {
                <ng-container
                  *ngTemplateOutlet="
                    legendItem;
                    context: {
                      colorClass: 'bg-shikenso-info-base',
                      labelKey: 'shared.statuses.planned',
                      count: contentTypesData().processing,
                      minWidth: 140,
                      maxWidth: 152,
                      fontClass: 'text-base',
                      dotSizeClass: 'w-3 h-3',
                      clickFn: onContentTypeLegendClicked.bind(this, 'processing'),
                    }
                  "
                />
              }
            </div>
          </div>
        </div>

        <!-- Matched Content Status bar graph -->
        @if (campaignExportConfig().showOverviewContentStatus) {
          <div class="flex flex-col flex-1">
            <span class="font-bold">
              {{ 'pages.campaignDetails.overview.matchedContentStatus' | translate: { count: matchedContentTotal() } }}
            </span>
            <div class="flex items-center flex-1">
              <app-content-status-bar-graph
                [items]="visibleMatchedContentStatusItems()"
                [loading]="loading()"
                [maxWidth]="268"
                [barHeight]="20"
                (itemClicked)="onMatchedContentStatusItemClicked($event)"
              />
            </div>
          </div>
        }
      </div>
    </div>
  }
  <!-- end @if showOverviewContentExecution -->

  <!-- Bottom row: Deliverables card + KPI card -->
  <div class="grid grid-cols-2 gap-4">
    <!-- Matched Deliverables Breakdown card -->
    @if (campaignExportConfig().showOverviewDeliverablesCard) {
      <div class="rounded-2xl bg-shikenso-gray-base-24 px-6 py-3 flex flex-col gap-4">
        <span class="text-sm font-medium">
          {{ 'pages.campaignDetails.overview.matchedDeliverablesBreakdown' | translate }}
        </span>
        <div class="flex items-center gap-16">
          <app-circular-graph
            totalCountFontClass="text-2xl leading-none"
            totalLabelFontClass="text-xs"
            statusType="deliverable"
            [data]="deliverablesData()"
            [loading]="loading()"
            [showTotal]="true"
            [chartSize]="154"
            [showPercentage]="false"
            [totalCountCentered]="true"
            [colorMap]="{ processing: '#cbcbcb' }"
            (segmentClicked)="onDeliverableSegmentClicked($event)"
          />
          <div class="flex flex-col gap-2 self-stretch w-full justify-center">
            @if (isDeliverableFailedVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-error-bright',
                    labelKey: 'shared.statuses.failed',
                    count: deliverablesData().failed,
                    minWidth: 120,
                    clickFn: onDeliverableLegendClicked.bind(this, 'failed'),
                  }
                "
              />
            }
            @if (isDeliverableProblemVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-warning-base',
                    labelKey: 'shared.statuses.problem',
                    count: deliverablesData().problem,
                    minWidth: 120,
                    clickFn: onDeliverableLegendClicked.bind(this, 'problem'),
                  }
                "
              />
            }
            @if (isDeliverableOkVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-success-bright',
                    labelKey: 'shared.statuses.ok',
                    count: deliverablesData().success,
                    minWidth: 120,
                    clickFn: onDeliverableLegendClicked.bind(this, 'success'),
                  }
                "
              />
            }
            @if (isDeliverableProcessingVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-gray-400',
                    labelKey: 'shared.statuses.processing',
                    count: deliverablesData().processing,
                    minWidth: 120,
                    clickFn: onDeliverableLegendClicked.bind(this, 'processing'),
                  }
                "
              />
            }
          </div>
        </div>
      </div>
    }

    <!-- Matched KPI Goal Performance card -->
    @if (campaignExportConfig().showOverviewKpiCard) {
      <div class="rounded-2xl bg-shikenso-gray-base-24 px-6 py-3 flex flex-col gap-4">
        <span class="text-sm font-medium">
          {{ 'pages.campaignDetails.overview.matchedKpiGoalPerformance' | translate }}
        </span>
        <div class="flex items-center gap-16">
          <app-circular-graph
            totalCountFontClass="text-2xl leading-none"
            totalLabelFontClass="text-xs"
            statusType="kpi"
            [data]="kpiGoalData()"
            [loading]="loading()"
            [showTotal]="true"
            [chartSize]="154"
            [showPercentage]="false"
            [totalCountCentered]="true"
            (segmentClicked)="onKpiGoalSegmentClicked($event)"
          />
          <div class="flex flex-col gap-2 self-stretch w-full justify-center">
            @if (isKpiFailedVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-error-bright',
                    labelKey: 'shared.statuses.kpiGoalsUnavailable',
                    count: kpiGoalData().failed,
                    minWidth: 120,
                    clickFn: onKpiGoalLegendClicked.bind(this, 'failed'),
                  }
                "
              />
            }
            @if (isKpiProblemVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-warning-base',
                    labelKey: 'shared.statuses.kpiGoalsBelowTarget',
                    count: kpiGoalData().problem,
                    minWidth: 120,
                    clickFn: onKpiGoalLegendClicked.bind(this, 'problem'),
                  }
                "
              />
            }
            @if (isKpiOkVisible()) {
              <ng-container
                *ngTemplateOutlet="
                  legendItem;
                  context: {
                    colorClass: 'bg-shikenso-success-bright',
                    labelKey: 'shared.statuses.kpiGoalsReached',
                    count: kpiGoalData().success,
                    minWidth: 120,
                    clickFn: onKpiGoalLegendClicked.bind(this, 'success'),
                  }
                "
              />
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>

<ng-template
  #legendItem
  let-colorClass="colorClass"
  let-labelKey="labelKey"
  let-count="count"
  let-minWidth="minWidth"
  let-fontClass="fontClass"
  let-dotSizeClass="dotSizeClass"
  let-maxWidth="maxWidth"
  let-clickFn="clickFn"
>
  @let isClickable = !!clickFn && count > 0;

  <div
    class="flex items-center gap-2 w-full px-1"
    [class.cursor-pointer]="isClickable"
    [class.rounded]="isClickable"
    [class.hover:bg-shikenso-pink-base-12]="isClickable"
    [style.minWidth.px]="minWidth ?? 120"
    [style.maxWidth.px]="maxWidth ?? 224"
    [attr.role]="isClickable ? 'button' : null"
    [attr.tabindex]="isClickable ? '0' : null"
    (click)="isClickable && clickFn()"
    (keydown.enter)="isClickable && clickFn()"
    (keydown.space)="isClickable && clickFn(); $event.preventDefault()"
  >
    <span class="rounded-full shrink-0" [ngClass]="dotSizeClass ?? 'w-[10px] h-[10px]'" [class]="colorClass"></span>
    <span [ngClass]="fontClass ?? 'text-sm'" class="grow">{{ labelKey | translate }}</span>

    @if (loading()) {
      <ngx-skeleton-loader class="flex h-[14px]" [theme]="{ height: '14px', width: '20px', 'border-radius': '4px' }" />
    } @else {
      <span class="shrink-0" [ngClass]="fontClass ?? 'text-sm'">{{ count }}</span>
    }
  </div>
</ng-template>
